import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { ToastMessage } from 'primereact/toast';
import { CASE_STATUS_TAG, type ApiTagType } from '../constants';

/**
 * An endpoint lock refuses a set of endpoints in this tab for as long as something holds it on. The base query does
 * the refusing (see `api.ts`) and `useEndpointLockToasts` explains it, so callers only need to skip their own error
 * toast for it (`isEndpointLockError`).
 *
 * To lock endpoints for another reason: add an id here, define it in ENDPOINT_LOCKS, and call
 * `useEndpointLock(id, isActive)` from the page that knows when it applies. A lock that follows a case/status flag,
 * like CASE_CLONING, can sit next to it in `useCaseCloningLock`, which already keeps case/status fresh.
 */
export const EndpointLock = {
  CASE_CLONING: 'caseCloning',
} as const;

export type EndpointLockKeys = (typeof EndpointLock)[keyof typeof EndpointLock];

/** Window event fired each time something is refused for a lock: a request (here or by the backend), or a UI action */
export const ENDPOINT_LOCK_REFUSED_EVENT = 'endpointLockRefused';

export interface EndpointLockRefusedDetail {
  lockId: EndpointLockKeys;
}

interface EndpointLockDefinition {
  /** Paths as written in each endpoint's `url`, without the leading "/" or the query string */
  endpoints: ReadonlySet<string>;
  /** The `detail` the backend sends when it refuses one of these itself; refusals made here send the same */
  errorDetail: string;
  /** Kept as one object so that a second refusal replaces the toast on screen rather than stacking another */
  toast: ToastMessage;
  /** Refetched after a refusal, so a lock that has since come off, or that only the backend knew about, catches up */
  refreshTags: ApiTagType[];
}

/**
 * The routes the backend locks while case/status reports `isCloning` that act on the case open in this tab. All of
 * them are POSTs; reads, including the `bundle/sections` POST, keep working. The backend's other locked routes are
 * left to it to refuse - its refusal still gets the toast:
 * - the ones only Proclaim or the function app call;
 * - document/upload-complete, bulk-upload-complete and upload-cancelled, which finish an upload already in storage.
 *   The lock is per tab, so refusing them here could strand an upload from another case still finishing in this tab;
 * - document/assign-case and the /unassigned routes, which act on documents outside the open case and are only
 *   called from the Medical Records list.
 */
const CASE_CLONING_LOCKED_ENDPOINTS = [
  // Bundles and bundle pages
  'bundle/add-to-light',
  'bundle/category/add',
  'bundle/extract-to',
  'bundle/mark/blank',
  'bundle/mark/breach',
  'bundle/mark/not-blank',
  'bundle/pages/slot-in',
  'bundle/provider/reorder',
  'bundle/provider/sort',
  'bundle/remove-from-light',
  'bundle/reorder',
  'bundle/reset-download',
  'bundle/retrieve-missing',
  'bundle/section/reorder',
  'bundle_page/category/reassign',
  // Pages and clusters
  'pages/rotate',
  'cluster/date/update',
  // Case
  'case/edit',
  'case/case-info/edit',
  'case/allegation/get-upload',
  'case/allegation/delete',
  'case/page-count',
  'case/start-pagination',
  'case/close',
  'case/clone',
  'case/transfer',
  // Chronology
  'chronology/entry/create',
  'chronology/entry/create-from-bundle',
  'chronology/entry/delete',
  'chronology/entry/comment/update',
  'chronology/entry/date/update',
  'chronology/entry/time/update',
  'chronology/entry/summary/update',
  'chronology/entry/pages/update',
  'chronology/entry/provider/update',
  'chronology/entry/provider/update/new',
  'chronology/entries/reorder',
  'chronology/entries/slot-in',
  // Documents
  'document/create',
  'document/bulk-create',
  'document/get-upload',
  'document/bulk-get-upload',
  'document/delete',
  'document/provider/add',
  'document/provider/add/new',
  // GDPR
  'gdpr/start-review',
  'gdpr/complete-review',
  'gdpr/clear-flags',
  'gdpr/mark/blank',
  'gdpr/mark/breach',
  'gdpr/mark-for-ai',
  // Medical match
  'medical-match/rerun',
  'medical-match-custom/add',
  'medical-match-custom/edit',
  'medical-match-custom/delete',
  'medical-match-custom/reset-answered-status',
  'medical-match-custom/send-live-questions',
  // Undo / redo
  'undo/bundle',
  'undo/chronology',
  'undo/document',
  'redo/bundle',
  'redo/chronology',
  'redo/document',
  // Admin
  'debug/reset-custom-ordering',
];

export const ENDPOINT_LOCKS: Record<EndpointLockKeys, EndpointLockDefinition> = {
  [EndpointLock.CASE_CLONING]: {
    endpoints: new Set(CASE_CLONING_LOCKED_ENDPOINTS),
    errorDetail: 'Cannot perform this operation whilst case is being cloned',
    toast: {
      severity: 'warn',
      summary: 'Cloning in progress',
      detail: "This case is being cloned, so you can't do this right now. Please try again once it has finished.",
      closable: true,
      life: 5000,
    },
    refreshTags: [CASE_STATUS_TAG],
  },
};

const ENDPOINT_LOCK_IDS = Object.values(EndpointLock);

/** e.g. both `'/bundle/category/add'` and `'bundle/category/add?bundle_id=1'` give `'bundle/category/add'` */
export const getEndpointPath = (args: string | FetchArgs): string =>
  (typeof args === 'string' ? args : args.url).split('?')[0].replace(/^\/+/, '');

/** The first lock that is on and covers `path`, if any */
export const findEndpointLock = (
  path: string,
  isLockActive: (lockId: EndpointLockKeys) => boolean,
): EndpointLockKeys | undefined =>
  ENDPOINT_LOCK_IDS.find((lockId) => ENDPOINT_LOCKS[lockId].endpoints.has(path) && isLockActive(lockId));

/**
 * What the base query returns for a request it refuses. It carries the backend's `data.detail`, but its status is
 * CUSTOM_ERROR rather than 409, so check for either refusal with `isEndpointLockError`, not the status.
 */
export const createEndpointLockError = (lockId: EndpointLockKeys): FetchBaseQueryError => {
  const { errorDetail } = ENDPOINT_LOCKS[lockId];

  return { status: 'CUSTOM_ERROR', error: errorDetail, data: { detail: errorDetail } };
};

/**
 * The lock a request was refused for, whether it was refused here or by the backend. Takes what `.unwrap()` throws
 * as well as the `{ error }` that `queryFulfilled` rejects with.
 */
export const getEndpointLockFromError = (error: unknown): EndpointLockKeys | undefined => {
  const queryError = (error as { error?: unknown } | null | undefined)?.error;
  const baseQueryError = queryError && typeof queryError === 'object' ? queryError : error;
  const detail = (baseQueryError as { data?: { detail?: unknown } | null } | null | undefined)?.data?.detail;

  if (typeof detail !== 'string') return undefined;

  return ENDPOINT_LOCK_IDS.find((lockId) => detail.includes(ENDPOINT_LOCKS[lockId].errorDetail));
};

/** True when a request was refused by an endpoint lock - its toast has already been shown */
export const isEndpointLockError = (error: unknown): boolean => getEndpointLockFromError(error) !== undefined;

/**
 * Has `useEndpointLockToasts` show the lock's toast. The base query calls this for every refused request; call it
 * directly for an action the UI refuses itself because of the lock, such as files dropped on a case being cloned.
 */
export const announceEndpointLockRefusal = (lockId: EndpointLockKeys) => {
  window.dispatchEvent(new CustomEvent<EndpointLockRefusedDetail>(ENDPOINT_LOCK_REFUSED_EVENT, { detail: { lockId } }));
};
